import ResizeObserver from 'resize-observer-polyfill';

import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';

export interface DocumentOverflowDetectionConfig {
    getTabsContainer: () => HTMLElement | undefined;
    getHeaderRight: () => HTMLElement | undefined;
    getUploadDate: () => HTMLElement | undefined;
    getRequirementName: () => HTMLElement | undefined;
    documents: VerificationDocument[];
    getDocumentName: (index: number) => string;
    onOverflowChange: (isOverflow: boolean) => void;
}

export class DocumentOverflowDetection {
    private resizeObserver: ResizeObserver | null = null;
    private uploadDateWidth = 0;
    private requirementNameWidth = 0;
    private tabsOverflow = false;
    public readonly handleWindowResize: () => void;

    public constructor(private config: DocumentOverflowDetectionConfig) {
        this.handleWindowResize = () => {
            requestAnimationFrame(() => this.checkTabsOverflow());
        };
    }

    public measureUploadDateWidth(): void {
        const uploadDate = this.config.getUploadDate();
        if (uploadDate) {
            // Store the width including the gap (20px)
            this.uploadDateWidth = uploadDate.offsetWidth + 20;
        }
    }

    public measureRequirementNameWidth(): void {
        const requirementName = this.config.getRequirementName();
        if (requirementName) {
            // Store the width including the gap (20px)
            this.requirementNameWidth = requirementName.offsetWidth + 20;
        }
    }

    private isElementWithClosest(el: unknown): el is Element {
        return el != null && typeof (el as Element).closest === 'function';
    }

    public setup(): void {
        const tabsContainer = this.config.getTabsContainer();
        if (!this.isElementWithClosest(tabsContainer)) {
            return;
        }
        // Observe the parent header content for size changes
        const headerContent = tabsContainer.closest('.Modal__headerContent') as HTMLElement | null;
        const targetElement = headerContent || tabsContainer;

        this.resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => this.checkTabsOverflow());
        });
        this.resizeObserver.observe(targetElement);
        // Initial check
        this.checkTabsOverflow();
    }

    public cleanup(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    public checkTabsOverflow(): void {
        const tabsContainer = this.config.getTabsContainer();
        const headerRight = this.config.getHeaderRight();

        if (!this.isElementWithClosest(tabsContainer) || this.config.documents.length <= 1) {
            this.updateOverflowState(false);
            return;
        }

        // Get the modal header element (parent of headerContent)
        const modalHeader = tabsContainer.closest('.Modal__header') as HTMLElement | null;
        const headerContent = tabsContainer.closest('.Modal__headerContent') as HTMLElement | null;
        if (!modalHeader || !headerContent) {
            return;
        }

        // Get the close button width (including margins and flex gap if inside headerContent)
        const closeButton = (modalHeader.querySelector('.Modal__headerClose')
            || headerContent.querySelector('[data-modal-close]')) as HTMLElement | null;
        let closeButtonWidth = 0;
        if (closeButton) {
            const closeStyle = getComputedStyle(closeButton);
            closeButtonWidth = closeButton.offsetWidth
                + parseFloat(closeStyle.marginLeft)
                + parseFloat(closeStyle.marginRight);
            // When close button is inside headerContent (TprmModal), it adds a flex gap
            if (headerContent.contains(closeButton)) {
                const containerGap = parseFloat(getComputedStyle(headerContent).gap) || 0;
                closeButtonWidth += containerGap;
            }
        }

        // Calculate available width for tabs
        // Use modalHeader width as the reference — headerContent may overflow it
        // (flex: 1 0 means flex-shrink: 0, so headerContent.clientWidth can exceed the visible area)
        const containerWidth = modalHeader.clientWidth;
        // Always use the stored uploadDateWidth to avoid feedback loop
        // headerRight currently might not include upload date if it's hidden
        const currentHeaderRightWidth = headerRight ? headerRight.offsetWidth : 0;
        // Add back uploadDateWidth if it's not currently included (when tabsOverflow is true)
        const fullHeaderRightWidth = this.tabsOverflow
            ? currentHeaderRightWidth + this.uploadDateWidth
            : currentHeaderRightWidth;
        // Always include requirement name width in calculation (even when hidden in overflow mode)
        const gap = 20;
        const availableWidth =
            containerWidth - closeButtonWidth - fullHeaderRightWidth - this.requirementNameWidth - gap;

        // Measure natural width using an off-screen measurement element
        const tabsNaturalWidth = this.measureTabsNaturalWidth();

        this.updateOverflowState(tabsNaturalWidth > availableWidth);
    }

    private measureTabsNaturalWidth(): number {
        // Create an off-screen container to measure true text widths
        const measureContainer = document.createElement('div');
        measureContainer.style.cssText = `
            position: absolute;
            visibility: hidden;
            height: auto;
            width: auto;
            white-space: nowrap;
            font-size: 14px;
            font-family: inherit;
        `;
        document.body.appendChild(measureContainer);

        let totalWidth = 0;

        // Measure each tab's natural width
        for (let i = 0; i < this.config.documents.length; i++) {
            const tabName = this.config.getDocumentName(i);
            measureContainer.textContent = tabName;
            const textWidth = measureContainer.offsetWidth;
            // Add padding (20px each side = 40px)
            totalWidth += textWidth + 40;
        }

        document.body.removeChild(measureContainer);
        return totalWidth;
    }

    private updateOverflowState(isOverflow: boolean): void {
        if (this.tabsOverflow !== isOverflow) {
            this.tabsOverflow = isOverflow;
            this.config.onOverflowChange(isOverflow);
        }
    }
}
